import { ToggleGroup } from '@interstate/components/ToggleGroup/ToggleGroup/ToggleGroup';
import { Typography } from '@interstate/components/Typography';
import { RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type { ResidualValueType } from '@makemydeal/dr-platform-types';

import {
    RESIDUAL_TYPE_DOLLAR_TESTID,
    RESIDUAL_TYPE_PERCENT_TESTID,
    RESIDUAL_TYPE_TOGGLE_GROUP_ID,
    RESIDUAL_TYPE_TOGGLE_TESTID
} from './constants';

export type ResidualTypeToggleProps = {
    selectedType: ResidualValueType;
    onChange: (residualType: ResidualValueType) => void;
    disabled?: boolean;
    label?: string;
};

export const ResidualTypeToggle = ({ selectedType, onChange, disabled = false, label }: ResidualTypeToggleProps) => {
    const isPercent = selectedType === RESIDUAL_PERCENT;

    // Interstate ToggleGroup forwards neither `disabled` nor `sx`, so disabled state is applied on the
    // wrapper (dim + aria-disabled) and by omitting the option click handlers.
    return (
        <div
            aria-disabled={disabled || undefined}
            aria-label={label}
            style={{ display: 'flex', flexDirection: 'column', height: '100%', ...(disabled ? { opacity: 0.5 } : {}) }}
        >
            {label && (
                <Typography tag="div" variant="label-md" sx={{ marginBottom: '0.25rem' }}>
                    {label}
                </Typography>
            )}
            <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                <ToggleGroup
                    size="sm"
                    id={RESIDUAL_TYPE_TOGGLE_GROUP_ID}
                    data-testid={RESIDUAL_TYPE_TOGGLE_TESTID}
                    toggleButtonOptions={[
                        {
                            id: 'residualTypeDollarToggle',
                            value: '$',
                            children: <span>$</span>,
                            selected: !isPercent,
                            'data-testid': RESIDUAL_TYPE_DOLLAR_TESTID,
                            ...(!disabled && {
                                onClick: () => {
                                    if (isPercent) {
                                        onChange(RESIDUAL_DOLLAR);
                                    }
                                }
                            })
                        },
                        {
                            id: 'residualTypePercentToggle',
                            value: '%',
                            children: <span>%</span>,
                            selected: isPercent,
                            'data-testid': RESIDUAL_TYPE_PERCENT_TESTID,
                            ...(!disabled && {
                                onClick: () => {
                                    if (!isPercent) {
                                        onChange(RESIDUAL_PERCENT);
                                    }
                                }
                            })
                        }
                    ]}
                    // ResidualField uses px, so we match it for consistency. Future work should probably use rem.
                    toggleWidth="60px"
                />
            </div>
        </div>
    );
};
